import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Play, Apple, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { localApi } from "../lib/localApi";
import { formatError } from "../lib/errors";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

export const useMobileApps = () => useQuery({ queryKey: ["public-mobile-apps"], queryFn: async () => (await localApi.get("/public/mobile-apps")).data, staleTime: 600_000, retry: 1 });

/* Badges store (Google Play / App Store) : lien réel si publié, sinon « Bientôt ». */
export function StoreBadges({ apps, dark = true, testid = "store-badges" }) {
  const items = [["Google Play", apps?.android_url, Play, "store-badge-android"], ["App Store", apps?.ios_url, Apple, "store-badge-ios"]];
  const base = dark ? "border-white/15 text-white" : "border-border text-foreground";
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testid}>
      {items.map(([label, url, Icon, tid]) => url ? (
        <a key={label} href={url} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-2 rounded-lg border ${base} px-3 py-1.5 text-left leading-tight transition-colors hover:border-brass`} data-testid={tid}>
          <Icon className="h-4 w-4 text-brass" />
          <span className="flex flex-col"><span className={`text-[9px] uppercase tracking-wider ${dark ? "text-slate-400" : "text-muted-foreground"}`}>Disponible sur</span><span className="text-xs font-semibold">{label}</span></span>
        </a>
      ) : (
        <span key={label} className={`inline-flex flex-col rounded-lg border ${base} px-3 py-1.5 text-left leading-tight opacity-60`} data-testid={`${tid}-soon`}><span className={`text-[9px] uppercase tracking-wider ${dark ? "text-slate-400" : "text-muted-foreground"}`}>Bientôt sur</span><span className="text-xs font-semibold">{label}</span></span>
      ))}
    </div>
  );
}

/* Édition des liens store — visible uniquement par l'administrateur Casanéo (la requête PUT est refusée sinon). */
export function MobileAppsLinksEditor() {
  const qc = useQueryClient();
  const { data } = useMobileApps();
  const { data: platform, isError } = useQuery({ queryKey: ["platform-agencies"], queryFn: async () => (await localApi.get("/platform/agencies")).data, retry: false });
  const isPlatformAdmin = !!platform && !isError;
  const [f, setF] = useState({ android_url: "", ios_url: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (data) setF({ android_url: data.android_url || "", ios_url: data.ios_url || "" }); }, [data]);
  if (!isPlatformAdmin) return null;
  const save = async () => {
    setBusy(true);
    try { await localApi.put("/platform/mobile-apps", f); qc.invalidateQueries({ queryKey: ["public-mobile-apps"] }); toast.success("Liens des stores enregistrés — visibles sur casaneo.pro et dans les e-mails de bienvenue"); }
    catch (e) { toast.error(formatError(e, "Enregistrement impossible")); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-dashed border-border p-3 space-y-2 text-xs" data-testid="mobile-apps-links-editor">
      <p className="font-medium text-foreground">Liens des stores (administrateur Casanéo)</p>
      <p className="text-muted-foreground">Fiche Google Play : Play Console → votre app → « Voir sur Google Play », ou depuis le Play Store sur votre téléphone → ⋮ → Partager → Copier le lien. Format : https://play.google.com/store/apps/details?id=…</p>
      <div className="grid sm:grid-cols-2 gap-2">
        <Input value={f.android_url} onChange={(e) => setF({ ...f, android_url: e.target.value })} placeholder="https://play.google.com/store/apps/details?id=…" className="h-8 text-xs" data-testid="mobile-apps-android-url" />
        <Input value={f.ios_url} onChange={(e) => setF({ ...f, ios_url: e.target.value })} placeholder="https://apps.apple.com/… (quand disponible)" className="h-8 text-xs" data-testid="mobile-apps-ios-url" />
      </div>
      <Button size="sm" className="h-8 gap-1.5 bg-[#0f172a]" onClick={save} disabled={busy} data-testid="mobile-apps-save">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Enregistrer</Button>
    </div>
  );
}
